import React, { useEffect, useRef, useState } from "react";
import {
  SuprSendProvider,
  useFeedClient,
  SuprSendFeedProvider,
} from "@suprsend/react";
import type { IRemoteNotification } from "@suprsend/web-sdk";
import { useUserDetailsContext } from "../context/userDetailsContext";
import { useGetOrderMediaConfig } from "../views/PlaceOrderTest/queryHooks";
import {
  useInboxNotificationSound,
  type InboxNotificationSoundConfig,
} from "../utils/notificationSound";
import { getEtaAlertPayloadFromNotification } from "../utils/suprSendFeedIntent";
import {
  EtaAlertProvider,
  useEtaAlertOptional,
} from "../context/etaAlertContext";

interface SuprSendContextProps {
  children: React.ReactNode;
}

function NotificationHandler({
  mobileConfigs,
}: {
  mobileConfigs: InboxNotificationSoundConfig;
}) {
  const feedClient = useFeedClient();
  const playNotification = useInboxNotificationSound(mobileConfigs);
  const etaAlert = useEtaAlertOptional();
  const etaAlertRef = useRef(etaAlert);

  useEffect(() => {
    etaAlertRef.current = etaAlert;
  }, [etaAlert]);

  useEffect(() => {
    if (!feedClient) return;

    const handleNewNotification = (data: IRemoteNotification) => {
      try {
        const etaPayload = getEtaAlertPayloadFromNotification(data);
        const alertApi = etaAlertRef.current;
        if (etaPayload && alertApi) {
          const openPayload = {
            orderId: etaPayload.orderId,
            orderNo: etaPayload.orderNo,
            eta: etaPayload.eta,
          };
          if (etaPayload.etaAlertType === "hard") {
            alertApi.openHardAlert(openPayload);
          } else {
            alertApi.openSoftAlert(openPayload);
          }
          // Soft/Hard are realtime UI events — skip default inbox sound routing noise
          return;
        }

        if (etaPayload && !alertApi) {
          console.warn(
            "ETA alert notification received but EtaAlertProvider is not available",
            etaPayload,
          );
        }

        playNotification(data);
      } catch (error) {
        console.error("Failed to handle feed notification:", error);
      }
    };

    feedClient.emitter.on("feed.new_notification", handleNewNotification);

    return () => {
      feedClient.emitter.off("feed.new_notification", handleNewNotification);
    };
  }, [feedClient, playNotification]);

  return null;
}

const SuprSendContext: React.FC<SuprSendContextProps> = ({ children }) => {
  const userDetails = useUserDetailsContext();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);
  const publicApiKey = mobileConfigs?.suprsend_orca_public_api_key;

  useEffect(() => {
    if (mobileConfigs && publicApiKey) {
      setIsConfigLoaded(true);
    }
  }, [mobileConfigs, publicApiKey]);

  // Hoist EtaAlertProvider so it does not remount when SuprSend config finishes loading
  const isSuprSendReady = Boolean(
    userDetails?._id && isConfigLoaded && publicApiKey,
  );

  return (
    <EtaAlertProvider>
      {isSuprSendReady ? (
        <SuprSendProvider
          publicApiKey={publicApiKey!}
          distinctId={userDetails!._id}
        >
          <SuprSendFeedProvider>
            <NotificationHandler mobileConfigs={mobileConfigs ?? null} />
            {children}
          </SuprSendFeedProvider>
        </SuprSendProvider>
      ) : (
        children
      )}
    </EtaAlertProvider>
  );
};

export default SuprSendContext;
